import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useSeo } from "@/lib/seo";
import { Button } from "@/components/ui/button";
import {
  Buildings, MapPin, SealCheck, UsersThree, ChatCircleDots, CalendarStar, GraduationCap,
  ArrowRight, Sparkle, Heart, Clock, UsersFour, Crown, PencilSimple, BookOpen, Camera,
} from "@phosphor-icons/react";

const CAMPUS = "https://static.prod-images.emergentagent.com/jobs/df41fdb8-3819-47be-a2f5-8e80426d9ef2/images/80bb4667b848b2f4f6a91dac355d779e40369a462c34fcf39b7df6649c681387.jpeg";
const DEAN = "https://static.prod-images.emergentagent.com/jobs/df41fdb8-3819-47be-a2f5-8e80426d9ef2/images/e0fea803beff6824d7e6d680250265a64042f88f975bfc92d57b547574dc2cad.jpeg";
const P = {
  a: "https://static.prod-images.emergentagent.com/jobs/df41fdb8-3819-47be-a2f5-8e80426d9ef2/images/01299c49d1acfbf5cf160926a1af9a4d7b94b95a4be884a0b0cd85ec606b4dbf.jpeg",
  b: "https://static.prod-images.emergentagent.com/jobs/df41fdb8-3819-47be-a2f5-8e80426d9ef2/images/de02b14dee886c3ed876bced80ee774a16bd9aba47886e1c8f0ea8e12965557b.jpeg",
  c: "https://static.prod-images.emergentagent.com/jobs/df41fdb8-3819-47be-a2f5-8e80426d9ef2/images/766c3fc42116d4d634fbb6dd777addd21bfa58aec2794a5f88ff25fa5ba79763.jpeg",
  d: "https://static.prod-images.emergentagent.com/jobs/df41fdb8-3819-47be-a2f5-8e80426d9ef2/images/517a19f681381a797f8c6e28a0246b6be007891615387180d1f75e36fc520189.jpeg",
};

const members = [
  { img: P.a, name: "Ananya Sharma", batch: "Class of 2016", role: "Product Designer" },
  { img: P.b, name: "Kenji Tanaka", batch: "Class of 2016", role: "Software Engineer" },
  { img: P.c, name: "Amara Okafor", batch: "Class of 2018", role: "Doctor" },
  { img: P.d, name: "Daniel Weber", batch: "Class of 2014", role: "Entrepreneur" },
];

const chat = [
  { who: "Ananya", img: P.a, msg: "Who's coming to the 10-year reunion in December? 🎉", me: false },
  { who: "Kenji", img: P.b, msg: "Wouldn't miss it! Booking flights this week.", me: false },
  { who: "You", img: P.a, msg: "Count me in — can we get a group photo at the old quad?", me: true },
];

const team = [
  { img: DEAN, name: "Dr. Meera Iyer", role: "Owner", roleCls: "bg-amber-500/15 text-amber-700", icon: Crown },
  { img: P.d, name: "Alumni Office", role: "Manager", roleCls: "bg-secondary/15 text-secondary", icon: UsersFour },
  { img: P.c, name: "Student Council", role: "Editor", roleCls: "bg-primary/10 text-primary", icon: PencilSimple },
];

const Section = ({ children, className = "" }) => (
  <motion.section initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }} className={className}>
    {children}
  </motion.section>
);

export default function SampleInstitution() {
  useSeo("A Sample Institution & Alumni Group", "See what an institution's page and alumni community look like on AlumniPulse.", null, { noindex: true });

  return (
    <div className="bg-background" data-testid="sample-institution-page">
      {/* Intro */}
      <section className="border-b border-border/60 bg-primary/[0.03]">
        <div className="mx-auto max-w-5xl px-5 py-12 text-center">
          <p className="overline text-secondary mb-3 inline-flex items-center gap-1.5"><Sparkle size={14} weight="fill" /> A guided preview</p>
          <h1 className="font-heading text-4xl font-light leading-[1.1] tracking-tight sm:text-5xl">
            This is an <span className="italic font-medium text-secondary">institution's home</span> on AlumniPulse.
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            A sample school/college page with its own alumni community, chat, reunions, memory wall and a team who manages it. This is a preview — build your real one in minutes.
          </p>
        </div>
      </section>

      {/* Institution header */}
      <div className="mx-auto max-w-6xl px-5 py-10">
        <Section>
          <div className="overflow-hidden rounded-3xl border border-border/60 bg-card shadow-sm">
            <img src={CAMPUS} alt="Campus" className="h-56 w-full object-cover sm:h-72" data-testid="sample-inst-cover" />
            <div className="p-6 sm:p-8">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="font-heading text-2xl font-medium sm:text-3xl">Riverside College</h2>
                    <span className="inline-flex items-center gap-1 rounded-full bg-secondary text-secondary-foreground px-2.5 py-0.5 text-xs font-medium"><SealCheck size={13} weight="fill" /> Officially managed</span>
                  </div>
                  <p className="mt-1 flex items-center gap-2 text-sm text-muted-foreground"><MapPin size={14} /> Portland, United States · Founded 1921</p>
                </div>
                <Link to="/register"><Button className="rounded-full gap-1.5" data-testid="sample-inst-join"><UsersThree size={16} /> Join alumni network</Button></Link>
              </div>
              <p className="mt-5 max-w-3xl leading-relaxed text-foreground/90">
                A community of makers, thinkers and doers since 1921. Home to 12,000+ alumni across 40 countries — reconnecting, mentoring and giving back to the next generation of Riverside students.
              </p>
              <div className="mt-6 grid grid-cols-2 gap-4 sm:max-w-xl sm:grid-cols-4">
                {[["12.4k", "Alumni"], ["38", "Batches"], ["6", "Communities"], ["40", "Countries"]].map(([n, l]) => (
                  <div key={l} className="rounded-xl border border-border/60 bg-muted/30 p-3 text-center">
                    <p className="font-heading text-xl font-semibold text-primary">{n}</p>
                    <p className="text-xs text-muted-foreground">{l}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Section>

        {/* Alumni group + chat */}
        <Section className="mt-12">
          <div className="grid gap-6 lg:grid-cols-2">
            <div>
              <p className="overline text-secondary mb-2 inline-flex items-center gap-1.5"><UsersThree size={14} weight="duotone" /> Alumni community</p>
              <h2 className="font-heading text-2xl font-light sm:text-3xl">The people who <span className="italic font-medium text-secondary">walked these halls.</span></h2>
              <p className="mt-2 text-muted-foreground">Every batch and society gets its own group — with members, a directory and a live chat room.</p>
              <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-2">
                {members.map((m) => (
                  <div key={m.name} className="flex items-center gap-3 rounded-2xl border border-border/60 bg-card p-3">
                    <img src={m.img} alt={m.name} className="h-12 w-12 rounded-full object-cover" />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{m.name}</p>
                      <p className="truncate text-xs text-muted-foreground">{m.role} · {m.batch}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* chat preview */}
            <div className="flex flex-col rounded-3xl border border-border/60 bg-card shadow-sm">
              <div className="flex items-center gap-2 border-b border-border/60 p-4">
                <span className="inline-grid h-9 w-9 place-items-center rounded-lg bg-accent text-primary"><ChatCircleDots size={18} weight="duotone" /></span>
                <div><p className="text-sm font-medium">Class of 2016 · Reunion</p><p className="text-xs text-secondary">14 online now</p></div>
              </div>
              <div className="flex-1 space-y-3 p-4">
                {chat.map((c, i) => (
                  <div key={i} className={`flex items-end gap-2 ${c.me ? "flex-row-reverse" : ""}`}>
                    <img src={c.img} alt={c.who} className="h-7 w-7 rounded-full object-cover" />
                    <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${c.me ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                      {!c.me && <p className="text-[11px] font-medium text-secondary">{c.who}</p>}
                      {c.msg}
                    </div>
                  </div>
                ))}
              </div>
              <div className="border-t border-border/60 p-3">
                <Link to="/register"><Button variant="outline" className="w-full rounded-full gap-1.5" data-testid="sample-inst-chat-cta"><ChatCircleDots size={15} /> Join the conversation</Button></Link>
              </div>
            </div>
          </div>
        </Section>

        {/* Reunion event */}
        <Section className="mt-12">
          <div className="grid gap-6 sm:grid-cols-2">
            <div className="flex items-start gap-4 rounded-2xl border border-border/60 bg-card p-6 shadow-sm">
              <div className="grid h-14 w-14 shrink-0 place-items-center rounded-xl bg-primary text-center text-primary-foreground">
                <div className="leading-none"><div className="text-[10px]">DEC</div><div className="font-heading text-xl font-semibold">12</div></div>
              </div>
              <div>
                <span className="inline-flex items-center gap-1 rounded-full bg-secondary/10 px-2 py-0.5 text-xs font-medium text-secondary"><CalendarStar size={12} weight="fill" /> Reunion</span>
                <h3 className="mt-1 font-heading text-lg font-medium">10-Year Reunion · Class of 2016</h3>
                <p className="text-sm text-muted-foreground">Riverside Quad · 182 going · Photo memories &amp; guest list</p>
              </div>
            </div>
            <div className="flex items-start gap-4 rounded-2xl border border-border/60 bg-card p-6 shadow-sm">
              <span className="inline-grid h-14 w-14 shrink-0 place-items-center rounded-xl bg-accent text-primary"><BookOpen size={26} weight="duotone" /></span>
              <div>
                <h3 className="font-heading text-lg font-medium">Departments &amp; courses</h3>
                <p className="text-sm text-muted-foreground">Browse programmes, faculty and batches — and find classmates from your exact course and year.</p>
              </div>
            </div>
          </div>
        </Section>

        {/* Memory wall snippet */}
        <Section className="mt-12">
          <div className="rounded-3xl border border-[#c9b48c]/50 bg-[#efe7d6] p-8 sm:p-10">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="overline mb-1 inline-flex items-center gap-1.5 text-[#8a6d3b]"><Clock size={14} weight="duotone" /> Memory Lane</p>
                <h2 className="font-heading text-2xl font-light tracking-tight text-[#4a3a22] sm:text-3xl">Every institution has its <span className="italic font-medium">own memory wall.</span></h2>
                <p className="mt-2 max-w-xl text-sm text-[#6b5836]">Old uniforms, classrooms, trophies and class photos — shared by the alumni who lived them.</p>
              </div>
              <Link to="/memory-lane"><Button className="rounded-full gap-1.5 bg-[#8a6d3b] text-white hover:bg-[#6b5836]" data-testid="sample-inst-memory-cta"><Camera size={15} /> See Memory Lane</Button></Link>
            </div>
            <div className="mt-6 grid gap-4 sm:grid-cols-3">
              {[["School Uniform", "The green blazers of the 80s"], ["Trophies & Awards", "State football champions '94"], ["Class Photograph", "Batch of 2016 farewell"]].map(([cat, t]) => (
                <div key={t} className="rounded-2xl border border-[#c9b48c]/40 bg-card/70 p-4">
                  <span className="rounded-full bg-[#8a6d3b]/15 px-2.5 py-0.5 text-xs font-medium text-[#8a6d3b]">{cat}</span>
                  <p className="mt-2 font-heading text-sm font-medium text-[#4a3a22]">{t}</p>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* Team */}
        <Section className="mt-12">
          <div className="mb-6 max-w-2xl">
            <p className="overline text-secondary mb-2 inline-flex items-center gap-1.5"><UsersFour size={14} weight="duotone" /> Managed by a team</p>
            <h2 className="font-heading text-2xl font-light sm:text-3xl">Run by the people who <span className="italic font-medium text-secondary">care about it.</span></h2>
            <p className="mt-2 text-muted-foreground">Institutions invite colleagues by email as Managers or Editors to keep the page and community thriving.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            {team.map((t) => (
              <div key={t.name} className="flex items-center gap-3 rounded-2xl border border-border/60 bg-card p-4">
                <img src={t.img} alt={t.name} className="h-12 w-12 rounded-full object-cover" />
                <div className="flex-1"><p className="text-sm font-medium">{t.name}</p></div>
                <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${t.roleCls}`}><t.icon size={12} weight="fill" /> {t.role}</span>
              </div>
            ))}
          </div>
        </Section>

        {/* CTA */}
        <Section className="mt-14 rounded-3xl bg-primary px-8 py-14 text-center text-primary-foreground sm:px-12">
          <Buildings size={36} weight="duotone" className="mx-auto opacity-90" />
          <h2 className="mt-4 font-heading text-3xl font-light sm:text-4xl">Give your institution a home like this.</h2>
          <p className="mx-auto mt-3 max-w-xl text-primary-foreground/80">Create your school or college page, invite your team, and bring your alumni together — free to start.</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/register"><Button size="lg" variant="secondary" className="rounded-full gap-2 px-8" data-testid="sample-inst-final-cta">Create your page <ArrowRight size={18} /></Button></Link>
            <Link to="/alumni-groups"><Button size="lg" variant="ghost" className="rounded-full px-8 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground">Explore groups</Button></Link>
          </div>
        </Section>
      </div>
    </div>
  );
}
